import { type AuthFetch, createAuthFetch, type ServiceClientOptions } from "@tcg/api-client";
import { useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { useAuth } from "@/auth/use-auth";

import { ApiConfigContext } from "./context";

/**
 * `fetch` for admin API calls: bearer token from the session, `401 → refresh once → retry`
 * (apps/packages/api-client auth interceptor, T11-02); a failed refresh switches the session to
 * "expired" (the page stays, the sign-in prompt opens).
 */
export function useAdminFetch(): AuthFetch {
  const config = useContext(ApiConfigContext);
  const { getAccessToken, refreshNow } = useAuth();
  return useMemo(
    () =>
      createAuthFetch({
        ...(config.fetch ? { fetch: config.fetch } : {}),
        getAccessToken,
        refresh: refreshNow,
      }),
    [config.fetch, getAccessToken, refreshNow],
  );
}

/** Options for any generated service client (`createCatalogClient(useClientOptions())`). */
export function useClientOptions(): ServiceClientOptions {
  const config = useContext(ApiConfigContext);
  const authFetch = useAdminFetch();
  const { getAccessToken } = useAuth();
  const { i18n } = useTranslation();
  const language = i18n.resolvedLanguage;
  return useMemo(
    () => ({
      baseUrl: config.baseUrl,
      fetch: authFetch,
      getAccessToken,
      getLocale: () => language,
    }),
    [config.baseUrl, authFetch, getAccessToken, language],
  );
}

/** A memoised typed client, e.g. `useServiceClient(createNotificationClient)`. */
export function useServiceClient<C>(factory: (options: ServiceClientOptions) => C): C {
  const options = useClientOptions();
  return useMemo(() => factory(options), [factory, options]);
}
